import styled, { css } from 'styled-components';
import { flexColumn } from '@stylesheets/helpers';
import {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_NARROW,
  SIDEBAR_MOBILE_BREAKPOINT,
} from '@constants/sidebar';

const MOBILE_BP = `${SIDEBAR_MOBILE_BREAKPOINT}px`;
const TRANSITION = '280ms cubic-bezier(0.4, 0, 0.2, 1)';

export const Wrapper = styled.div`
  ${flexColumn}
  height: 100vh;   /* fallback for browsers without dvh support */
  height: 100dvh;  /* tracks the actual visible viewport as browser chrome shows/hides */
  width: 100%;
  overflow: hidden;
`;

export const Content = styled.div`
  display: grid;
  flex: 1;
  /* Without it an oversize sidebar pushes Content, and the editor's bottom toolbar, below the viewport. */
  min-height: 0;
  overflow: hidden;
  width: 100%;
  grid-template-columns: ${({ $narrow }) =>
      $narrow ? `${SIDEBAR_WIDTH_NARROW}px` : `${SIDEBAR_WIDTH}px`}
    1fr;
  /* Bound the row to the container: an auto row grows with a too-tall sidebar and drags Main off-screen. */
  grid-template-rows: minmax(0, 1fr);
  transition: grid-template-columns ${TRANSITION};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${({ theme: { colors } }) => css`
    background-color: ${colors.surface};
  `}

  @media (max-width: ${MOBILE_BP}) {
    grid-template-columns: 1fr;
  }
`;

export const Sidebar = styled.aside`
  width: 100%;
  /* Without it a too-tall sidebar grows the grid row past the viewport. */
  min-height: 0;
  /* The containing block for overlays anchored to the menu's edge, e.g. the expand handle. */
  position: relative;
  /* Clipped so the async task list cannot push the menu header off-screen; the rails let overlays out. */
  overflow: ${({ $rail }) => ($rail ? 'visible' : 'hidden')};

  ${({ theme: { colors } }) => css`
    border-right: 1px solid ${colors.shellDivider};
  `}

  @media (max-width: ${MOBILE_BP}) {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 1200;
    width: min(360px, calc(100vw - 56px));
    /* none, not translateX(0): a transform would trap the drawer's fixed dialogs inside it. */
    transform: ${({ $mobileOpen }) => ($mobileOpen ? 'none' : 'translateX(-100%)')};
    /* Hidden once slid away so its links leave the tab order and nothing overhanging it peeks in. */
    visibility: ${({ $mobileOpen }) => ($mobileOpen ? 'visible' : 'hidden')};
    transition:
      transform ${TRANSITION},
      visibility 0s linear ${({ $mobileOpen }) => ($mobileOpen ? '0s' : '280ms')};

    ${({ theme: { colors } }) => css`
      background-color: ${colors.surface};
    `}
  }
`;

export const Backdrop = styled.div`
  display: none;

  @media (max-width: ${MOBILE_BP}) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1100;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
    transition: opacity ${TRANSITION};
  }
`;

export const Main = styled.div`
  /* Without it a page that forgets to clamp itself grows the grid row past the viewport. */
  min-height: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* The full apps gallery covers this column. */
  position: relative;

  ${({ theme: { colors } }) => css`
    background-color: ${colors.surface};
  `}
`;

export const MainContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  overflow: auto;
  position: relative;
`;

export const BannerWrapper = styled.div``;
